import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { apiClient, formatApiError } from "@/lib/auth";
import NexWordmark from "@/components/NexWordmark";
import {
  ArrowLeft, Search, Plus, Loader2, TrendingUp, Tag as TagIcon, Filter, RefreshCw,
  Send, X, MessageSquare, Sparkles,
} from "lucide-react";
import { toast } from "sonner";

const STAGE_COLORS = {
  new:                   "bg-white/5 border-white/10 text-nex-chrome",
  audit_submitted:       "bg-blue-500/10 border-blue-500/30 text-blue-300",
  proposal_sent:         "bg-nex-gold/10 border-nex-gold/40 text-nex-gold",
  proposal_viewed:       "bg-nex-gold/20 border-nex-gold/50 text-nex-gold",
  consultation_booked:   "bg-purple-500/10 border-purple-500/30 text-purple-300",
  deployment_signed:     "bg-emerald-500/10 border-emerald-500/30 text-emerald-300",
  retainer_active:       "bg-emerald-500/15 border-emerald-500/40 text-emerald-300",
  lost:                  "bg-red-500/10 border-red-500/30 text-red-300",
};

const ContactsListPage = () => {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [stages, setStages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [stage, setStage] = useState("");
  const [minScore, setMinScore] = useState(0);
  const [showAdd, setShowAdd] = useState(false);
  const [busy, setBusy] = useState(false);
  // Selection + broadcast
  const [selected, setSelected] = useState(new Set());
  const [showBcast, setShowBcast] = useState(false);
  const [bcastMsg, setBcastMsg] = useState("Hi {first_name}, quick update from NEX. Reply YES to schedule a call.");
  const [bcastGenLink, setBcastGenLink] = useState(false);
  const [bcastTitle, setBcastTitle] = useState("Your NEX update");
  const [bcastSending, setBcastSending] = useState(false);
  // add form
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [business, setBusiness] = useState("");
  const [phone, setPhone] = useState("");

  const load = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (search.trim()) params.set("search", search.trim());
      if (stage) params.set("stage", stage);
      if (minScore) params.set("min_score", String(minScore));
      const { data } = await apiClient.get(`/crm/contacts?${params.toString()}`);
      setItems(data.items);
      setStages(data.stages);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  }, [search, stage, minScore]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const backfill = async () => {
    setBusy(true);
    try {
      const { data } = await apiClient.post("/crm/contacts/backfill-from-briefs");
      toast.success(`Backfill complete — ${data.created} new, ${data.already_existed} already synced`);
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const addContact = async (e) => {
    e.preventDefault();
    if (!name || !email) return toast.error("Name and email required");
    setBusy(true);
    try {
      const { data } = await apiClient.post("/crm/contacts", {
        full_name: name, email, business_name: business, phone,
      });
      toast.success("Contact created");
      nav(`/admin/contacts/${data.contact.id}`);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const toggle = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    setSelected(prev => prev.size === items.length ? new Set() : new Set(items.map(c => c.id)));
  };

  const preview = (() => {
    const first = items.find(c => selected.has(c.id));
    if (!first) return bcastMsg;
    return bcastMsg
      .replace("{first_name}", (first.full_name || "").split(" ")[0] || "there")
      .replace("{business}", first.business_name || "")
      .replace("{link}", bcastGenLink ? "nexengines.com/c/abc123" : "");
  })();

  const sendBroadcast = async () => {
    if (selected.size === 0) return toast.error("Select at least one contact");
    if (!bcastMsg.trim()) return toast.error("Write a message");
    setBcastSending(true);
    try {
      const { data } = await apiClient.post("/crm/broadcast/sms", {
        contact_ids: Array.from(selected),
        message: bcastMsg,
        generate_link: bcastGenLink,
        link_title: bcastTitle,
      });
      const parts = [];
      if (data.sent) parts.push(`✓ ${data.sent} sent`);
      if (data.skipped_no_phone) parts.push(`⚠ ${data.skipped_no_phone} no phone`);
      if (data.gateway_error) parts.push(`⏸ ${data.gateway_error} gateway err`);
      if (data.failed) parts.push(`✗ ${data.failed} failed`);
      toast.success(`Broadcast complete — ${parts.join(" · ")}`);
      setShowBcast(false);
      setSelected(new Set());
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBcastSending(false); }
  };

  return (
    <div className="min-h-screen bg-[#0B0F19]">
      <div className="border-b border-nex-border bg-black/40 backdrop-blur">
        <div className="w-full mx-auto px-6 md:px-10 lg:px-16 xl:px-24 2xl:px-32 py-4 flex items-center gap-6">
          <Link to="/admin" className="flex items-center gap-3">
            <ArrowLeft size={14} className="text-nex-chrome" />
            <NexWordmark size="text-xl" />
            <span className="mono-body text-[10px] tracking-[0.25em] uppercase text-nex-gold">// CRM</span>
          </Link>
        </div>
      </div>

      <div className="w-full mx-auto px-6 md:px-10 lg:px-16 xl:px-24 2xl:px-32 py-8">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-6">
          <div>
            <div className="mono-label mb-2">// PIPELINE</div>
            <h1 className="serif-head text-3xl">Contacts</h1>
            <p className="text-nex-chrome mt-1 text-sm">
              Every audit submission, every open, every step completed — one thread per contact
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={backfill} disabled={busy} className="tick-btn-secondary text-xs !py-2 !px-3" data-testid="backfill-btn">
              <RefreshCw size={13} className={busy ? "animate-spin" : ""} /> Backfill briefs
            </button>
            <button onClick={() => setShowAdd(s => !s)} className="tick-btn-primary text-xs !py-2 !px-3" data-testid="add-contact-btn">
              <Plus size={13} /> Add contact
            </button>
          </div>
        </div>

        {showAdd && (
          <form onSubmit={addContact} className="card-industrial corner-tick p-5 mb-6 grid md:grid-cols-5 gap-3" data-testid="add-contact-form">
            <input value={name}     onChange={e => setName(e.target.value)}     placeholder="Full name"     className="input-industrial" data-testid="contact-name" />
            <input value={email}    onChange={e => setEmail(e.target.value)}    placeholder="Email"          className="input-industrial" data-testid="contact-email" type="email" />
            <input value={business} onChange={e => setBusiness(e.target.value)} placeholder="Business (opt)" className="input-industrial" data-testid="contact-business" />
            <input value={phone}    onChange={e => setPhone(e.target.value)}    placeholder="Phone (opt)"    className="input-industrial" data-testid="contact-phone" />
            <button type="submit" disabled={busy} className="tick-btn-primary justify-center" data-testid="contact-save">
              {busy ? <Loader2 size={13} className="animate-spin" /> : "Create"}
            </button>
          </form>
        )}

        {selected.size > 0 && (
          <div className="card-industrial corner-tick p-4 mb-4 bg-nex-gold/5 border-nex-gold/40 flex items-center justify-between gap-3 flex-wrap" data-testid="selection-bar">
            <div className="flex items-center gap-3">
              <span className="mono-label !text-nex-gold">// {selected.size} selected</span>
              <button onClick={() => setSelected(new Set())} className="text-nex-muted hover:text-nex-silver mono-body text-[10px] uppercase">Clear</button>
            </div>
            <button onClick={() => setShowBcast(true)} className="tick-btn-primary text-xs !py-2 !px-4" data-testid="broadcast-btn">
              <MessageSquare size={13} /> SMS Broadcast
            </button>
          </div>
        )}

        <div className="grid md:grid-cols-4 gap-3 mb-4" data-testid="contact-filters">
          <div className="relative md:col-span-2">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-nex-muted" />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search name, email, business, phone" className="input-industrial !pl-9" data-testid="search-input" />
          </div>
          <select value={stage} onChange={e => setStage(e.target.value)} className="input-industrial" data-testid="stage-filter">
            <option value="">All stages</option>
            {stages.map(s => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
          </select>
          <div className="flex items-center gap-2">
            <TrendingUp size={14} className="text-nex-muted" />
            <input type="number" value={minScore} onChange={e => setMinScore(Number(e.target.value) || 0)} placeholder="Min score" className="input-industrial" data-testid="score-filter" />
          </div>
        </div>

        <div className="card-industrial corner-tick overflow-hidden" data-testid="contacts-table">
          {loading ? (
            <div className="text-center py-16"><Loader2 className="animate-spin text-nex-gold mx-auto" /></div>
          ) : items.length === 0 ? (
            <div className="text-center py-16 text-nex-chrome text-sm">
              No contacts match. Try <button onClick={backfill} className="text-nex-gold underline">backfilling from briefs</button> or add one manually.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-black/30 border-b border-nex-border">
                  <tr className="mono-body text-[10px] uppercase tracking-[0.15em] text-nex-chrome">
                    <th className="text-left px-3 py-3 w-8">
                      <input
                        type="checkbox"
                        checked={selected.size === items.length && items.length > 0}
                        onChange={toggleAll}
                        className="accent-nex-gold"
                        data-testid="select-all"
                      />
                    </th>
                    <th className="text-left px-4 py-3">Contact</th>
                    <th className="text-left px-4 py-3">Business</th>
                    <th className="text-left px-4 py-3">Stage</th>
                    <th className="text-left px-4 py-3">Tags</th>
                    <th className="text-right px-4 py-3">Score</th>
                    <th className="text-left px-4 py-3">Last activity</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map(c => (
                    <tr key={c.id} onClick={() => nav(`/admin/contacts/${c.id}`)} className="border-b border-nex-border/50 hover:bg-white/[0.02] cursor-pointer" data-testid={`contact-row-${c.id}`}>
                      <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selected.has(c.id)}
                          onChange={() => toggle(c.id)}
                          className="accent-nex-gold"
                          data-testid={`select-${c.id}`}
                        />
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-nex-silver">{c.full_name}</div>
                        <div className="mono-body text-[10px] text-nex-muted">{c.email}</div>
                      </td>
                      <td className="px-4 py-3 text-nex-chrome">{c.business_name || <span className="text-nex-muted">—</span>}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center px-2 py-1 border ${STAGE_COLORS[c.stage] || STAGE_COLORS.new} mono-body text-[10px] tracking-[0.1em] uppercase`}>
                          {c.stage.replace(/_/g, " ")}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          {(c.tags || []).slice(0, 3).map(t => (
                            <span key={t} className="mono-body text-[10px] uppercase text-nex-gold border border-nex-gold/30 px-1.5 py-0.5">
                              <TagIcon size={9} className="inline mr-1" />{t}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right text-nex-gold font-medium">{c.lead_score || 0}</td>
                      <td className="px-4 py-3 mono-body text-[10px] text-nex-muted whitespace-nowrap">
                        {c.last_activity_at ? new Date(c.last_activity_at).toLocaleDateString() : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {showBcast && (
          <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4 overflow-y-auto" onClick={() => !bcastSending && setShowBcast(false)}>
            <div onClick={(e) => e.stopPropagation()} className="card-industrial corner-tick p-6 md:p-8 w-full max-w-lg relative my-8" data-testid="broadcast-modal">
              <button type="button" onClick={() => setShowBcast(false)} className="absolute top-4 right-4 text-nex-muted hover:text-nex-silver"><X size={16} /></button>
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="text-nex-gold" size={16} />
                <div className="mono-label">// SMS BROADCAST</div>
              </div>
              <h2 className="serif-head text-2xl mb-1">Send to {selected.size} contact{selected.size === 1 ? "" : "s"}</h2>
              <p className="text-nex-chrome text-sm mb-5">Placeholders: <code className="text-nex-gold">{"{first_name}"}</code> · <code className="text-nex-gold">{"{business}"}</code> · <code className="text-nex-gold">{"{link}"}</code></p>

              <label className="mono-label !text-[9px] block mb-1.5">Message</label>
              <textarea
                value={bcastMsg}
                onChange={(e) => setBcastMsg(e.target.value)}
                rows={4}
                maxLength={1500}
                className="input-industrial w-full mb-1"
                data-testid="broadcast-message"
              />
              <div className="mono-body text-[10px] text-nex-muted text-right mb-4">{bcastMsg.length}/1500</div>

              <label className="flex items-start gap-3 cursor-pointer mb-4" data-testid="broadcast-genlink-toggle">
                <input
                  type="checkbox"
                  checked={bcastGenLink}
                  onChange={(e) => setBcastGenLink(e.target.checked)}
                  className="mt-1 accent-nex-gold"
                />
                <div>
                  <div className="text-nex-silver text-sm">Generate a personalized ActionLink per contact</div>
                  <div className="mono-body text-[10px] text-nex-muted mt-1">
                    Each contact gets a unique <code>nexengines.com/c/&lt;token&gt;</code> — inject via <code>{"{link}"}</code>. Opens & taps auto-log to their timeline.
                  </div>
                </div>
              </label>

              {bcastGenLink && (
                <div className="mb-4">
                  <label className="mono-label !text-[9px] block mb-1.5">Link title (shown at top of the landing page)</label>
                  <input
                    value={bcastTitle}
                    onChange={(e) => setBcastTitle(e.target.value)}
                    className="input-industrial w-full"
                    data-testid="broadcast-link-title"
                  />
                </div>
              )}

              <div className="border border-nex-border p-3 mb-5 bg-black/30">
                <div className="mono-label !text-[9px] mb-2">// PREVIEW (first selected contact)</div>
                <div className="text-nex-silver text-sm whitespace-pre-wrap font-mono">{preview}</div>
              </div>

              <div className="flex gap-3">
                <button onClick={() => setShowBcast(false)} disabled={bcastSending} className="tick-btn-secondary flex-1 justify-center">Cancel</button>
                <button onClick={sendBroadcast} disabled={bcastSending || !bcastMsg.trim()} className="tick-btn-primary flex-1 justify-center" data-testid="broadcast-send">
                  {bcastSending ? <><Loader2 size={13} className="animate-spin" /> Sending</> : <><Send size={13} /> Broadcast to {selected.size}</>}
                </button>
              </div>
              <div className="mono-body text-[10px] text-nex-muted text-center mt-4">
                Sends are rate-limited (10s between each) so we don&apos;t trip carrier spam filters.
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ContactsListPage;
